(() => {
  'use strict';
  if (window.PetalCartFlow) return;
  const root = window.Shopify?.routes?.root || '/';
  const keys = { gift: 'Petal gift card message', special: 'Petal special instructions' };
  const initialized = new WeakSet();
  let values = null, version = 0, savedVersion = 0, note = '', timer, saving = null;
  let working = false, step = 'cart';
  const drawer = () => document.getElementById('MinimogCartDrawer');
  const compose = (gift, special) => [gift.trim() ? 'Gift card message:\n' + gift.trim() : '', special.trim() ? 'Special instructions:\n' + special.trim() : ''].filter(Boolean).join('\n\n');
  const mergeNote = (cart, current) => {
    const previous = compose(cart.attributes?.[keys.gift] || '', cart.attributes?.[keys.special] || '');
    let existing = cart.note || '';
    if (previous && existing.includes(previous)) existing = existing.replace(previous, '').trim();
    return [existing, compose(current.gift, current.special)].filter(Boolean).join('\n\n');
  };
  async function request(path, payload) {
    const response = await fetch(root + path, payload ? {
      method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(payload)
    } : { credentials: 'same-origin', cache: 'no-store' });
    const data = await response.json();
    if (!response.ok || data.status >= 400) throw new Error(data.description || data.message || 'Cart request failed');
    return data;
  }
  function showError(message) {
    const el = drawer()?.querySelector('[data-petal-error]');
    if (!el) return;
    el.textContent = message || '';
    el.hidden = !message;
  }
  function errorText() {
    return drawer()?.querySelector('[data-petal-messages]')?.dataset.errorText || 'Could not save your changes. Please try again before checkout.';
  }
  function updateNoteInputs() {
    drawer()?.querySelectorAll('[name="note"]').forEach(input => { input.value = note; });
  }
  function resizeMessage(input) {
    if (!input || !input.getClientRects().length) return;
    input.style.height = '48px';
    if (input.value) {
      const styles = window.getComputedStyle(input);
      const borders = (parseFloat(styles.borderTopWidth) || 0) + (parseFloat(styles.borderBottomWidth) || 0);
      input.style.height = Math.max(48, input.scrollHeight + borders) + 'px';
    }
  }
  function resizeMessages() {
    drawer()?.querySelectorAll('[data-petal-message]').forEach(resizeMessage);
  }
  function arrangeActions() {
    const el = drawer();
    if (!el) return;
    const back = el.querySelector('[data-petal-back]');
    const checkout = el.querySelector('[data-petal-checkout]');
    if (!back || !checkout || checkout.closest('.petal-cart-actions')) return;
    const actions = document.createElement('div');
    actions.className = 'petal-cart-actions';
    checkout.parentNode.insertBefore(actions, checkout);
    actions.append(back, checkout);
  }
  function syncStep(focus = false) {
    const el = drawer();
    if (!el) return;
    el.dataset.petalStep = step;
    const back = el.querySelector('[data-petal-back]');
    if (back) back.hidden = step !== 'delivery';
    const next = el.querySelector('[data-petal-continue]');
    if (next) next.hidden = step !== 'cart';
    if (step === 'cart') resizeMessages();
    if (focus) {
      const target = step === 'delivery' ? el.querySelector('[data-petal-back]') : el.querySelector('#PetalGiftMessage');
      target?.focus({ preventScroll: true });
      const footer = el.querySelector('#MinimogCartDrawerFooter');
      if (footer) footer.scrollTop = 0;
      const panel = el.querySelector('.m-cart-drawer__footer--wrapper');
      if (panel?.animate && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        panel.animate([{ transform: 'translateX(' + (step === 'delivery' ? '24px' : '-24px') + ')', opacity: 0 }, { transform: 'translateX(0)', opacity: 1 }], { duration: 220, easing: 'ease-out' });
      }
    }
  }
  function hydrate() {
    const el = drawer();
    if (!el) return;
    arrangeActions();
    const fields = el.querySelector('[data-petal-messages]');
    if (fields && !initialized.has(fields)) {
      initialized.add(fields);
      const gift = fields.querySelector('[data-petal-message="gift"]');
      const special = fields.querySelector('[data-petal-message="special"]');
      if (!values) {
        values = { gift: gift.value, special: special.value };
        note = fields.querySelector('[data-petal-note]').value;
      } else {
        gift.value = values.gift;
        special.value = values.special;
      }
      updateNoteInputs();
    }
    if (el.querySelector('.m-cart-drawer__empty')) {
      step = 'cart';
      if (!saving && !working) { values = null; version = 0; savedVersion = 0; note = ''; }
    }
    syncStep();
  }
  async function save() {
    clearTimeout(timer);
    if (saving) { await saving; if (version !== savedVersion) return save(); return; }
    if (!values) hydrate();
    if (!values) return;
    saving = (async () => {
      do {
        const snapshot = { ...values }, revision = version;
        const cart = await request('cart.js');
        const combined = mergeNote(cart, snapshot);
        const updated = await request('cart/update.js', {
          note: combined,
          attributes: { [keys.gift]: snapshot.gift, [keys.special]: snapshot.special }
        });
        if ((updated.note || '') !== combined) throw new Error('The order note was not saved');
        note = combined;
        savedVersion = revision;
        updateNoteInputs();
      } while (savedVersion !== version);
    })();
    try { await saving; showError(''); }
    finally { saving = null; }
  }
  function scheduleSave() {
    clearTimeout(timer);
    timer = setTimeout(() => save().catch(() => showError(errorText())), 500);
  }
  function busy(value) {
    working = value;
    const el = drawer();
    if (!el) return;
    el.setAttribute('aria-busy', String(value));
    el.querySelectorAll('[data-petal-continue], [data-petal-back]').forEach(button => { button.disabled = value; });
  }
  async function proceed() {
    if (working) return;
    busy(true); showError('');
    try { await save(); step = 'delivery'; syncStep(true); document.dispatchEvent(new CustomEvent('petal:delivery-open')); }
    catch { showError(errorText()); }
    finally { busy(false); }
  }
  function checkoutGuard(event) {
    const el = drawer();
    if (!el || event.defaultPrevented) return;
    const target = event.type === 'submit'
      ? event.submitter || el.querySelector('[data-petal-checkout]')
      : event.target.closest('[name="checkout"], .additional-checkout-buttons');
    if (!target || !el.contains(target)) return;
    if (event.type === 'submit' && event.target.id !== 'cart-drawer-form') return;

    /* The first-step checkout action is really Continue. Save the messages and open delivery. */
    if (step === 'cart') {
      event.preventDefault();
      event.stopImmediatePropagation();
      proceed();
      return;
    }

    /* Delivery step: do not intercept checkout. The existing delivery/cart-field code must receive the real click/submit so it can validate, flush attributes and let Shopify checkout normally. */
  }
  document.addEventListener('input', event => {
    const input = event.target.closest('[data-petal-message]');
    if (!input || !drawer()?.contains(input)) return;
    if (!values) hydrate();
    values[input.dataset.petalMessage] = input.value;
    version++;
    resizeMessage(input);
    scheduleSave();
  });
  document.addEventListener('change', event => {
    if (event.target.matches('[data-petal-message]')) scheduleSave();
  });
  document.addEventListener('click', event => {
    const button = event.target.closest('[data-petal-continue], [data-petal-back]');
    if (!button || !drawer()?.contains(button)) return;
    event.preventDefault();
    if (button.hasAttribute('data-petal-continue')) proceed();
    else if (button.hasAttribute('data-petal-back') && !working) { step = 'cart'; syncStep(true); }
  });
  window.addEventListener('click', checkoutGuard, true);
  document.addEventListener('submit', checkoutGuard, true);
  new MutationObserver(records => {
    if (records.some(record => record.addedNodes.length && (drawer()?.contains(record.target) || Array.from(record.addedNodes).some(node => node.nodeType === 1 && (node.id === 'MinimogCartDrawer' || node.querySelector?.('#MinimogCartDrawer')))))) hydrate();
  }).observe(document.documentElement, { childList: true, subtree: true });
  window.addEventListener('resize', resizeMessages);
  document.addEventListener('transitionend', resizeMessages);
  window.PetalCartFlow = { reset() { step = 'cart'; hydrate(); }, save };
  hydrate();
})();
